/**
 * Status derivation — the green / amber / red Christmas tree.
 *
 * The tree legend in the mock defines the rule the product actually means:
 *
 *   amber "due soon"  — before your next event
 *   green "good"      — within interval
 *   red   "overdue"   — past its trigger
 *
 * So "due" is not a percentage of an interval; it is "this lands on or before
 * the next time you race." These functions encode that, and take `today` as an
 * argument rather than reading the clock.
 */

import { addDays, compareIso, daysBetween } from './dates.js';
import type { IsoDate, Occurrence, Status } from './types.js';

/**
 * Fallback window for deciding "due soon" when a car has no event scheduled.
 * With nothing to be ready for, fall back to a calendar horizon.
 */
export const DEFAULT_DUE_WITHIN_DAYS = 30;

/** Reminder ladder for safety expirations (§6). */
export const EXPIRY_NOTICE_DAYS = [60, 30] as const;

export interface ItemStatusInput {
  /** Output of `projectOccurrences` for this item. */
  occurrences: readonly Occurrence[];
  today: IsoDate;
  /** The car's next event, if any — the deadline "due soon" is measured against. */
  nextEventDate?: IsoDate | null;
  dueWithinDays?: number;
}

/**
 * Red if a service was missed, amber if the next one lands on or before the next
 * event (or inside the fallback window), green otherwise.
 */
export function itemStatus(input: ItemStatusInput): Status {
  const { occurrences, today, nextEventDate } = input;
  const dueWithinDays = input.dueWithinDays ?? DEFAULT_DUE_WITHIN_DAYS;

  if (occurrences.some((o) => o.overdue)) return 'over';

  const next = occurrences.find((o) => !o.overdue);
  if (!next) return 'good';

  const deadline = nextEventDate ?? addDays(today, dueWithinDays);
  return compareIso(next.date, deadline) <= 0 ? 'due' : 'good';
}

export interface DateStatus {
  /** When it comes due. */
  date: IsoDate;
  /** Days from `today` until `date`. Negative once it has passed. */
  days: number;
  status: Status;
}

/**
 * Status of a date-certain item — a safety expiration or recert (§2, `is_safety`).
 * Amber once inside the first reminder notice (60 days), red once expired.
 */
export function expirationStatus(
  expirationDate: IsoDate,
  today: IsoDate,
  dueWithinDays: number = EXPIRY_NOTICE_DAYS[0],
): DateStatus {
  const days = daysBetween(today, expirationDate);
  return {
    date: expirationDate,
    days,
    status: days < 0 ? 'over' : days <= dueWithinDays ? 'due' : 'good',
  };
}

/**
 * Status of an inspect-by-hand item: last checked plus its window (§2, `inspect`).
 * A never-checked item is overdue by definition — nobody has ever looked at it.
 */
export function inspectStatus(
  lastCheckedAt: IsoDate | null,
  windowDays: number,
  today: IsoDate,
  dueWithinDays = 10,
): DateStatus {
  if (!lastCheckedAt) {
    return { date: today, days: 0, status: 'over' };
  }
  const date = addDays(lastCheckedAt, windowDays);
  const days = daysBetween(today, date);
  return {
    date,
    days,
    status: days < 0 ? 'over' : days <= dueWithinDays ? 'due' : 'good',
  };
}

/**
 * How far through its interval an item is, 0–100, for the progress bar.
 * Clamped at 100 so an overdue item shows a full bar rather than overflowing.
 */
export function meterPct(used: number, interval: number): number {
  if (!(interval > 0)) return 0;
  return Math.max(0, Math.min(100, Math.round((used / interval) * 100)));
}
